"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { getClaimedByDisplayName } from '../lib/claimed-by-display'
import { authFetchJson } from '@/shared/lib/auth-fetch'

interface ClaimedByOption {
  label: string
  value: string
}

interface OrganizationOptionsContextType {
  options: ClaimedByOption[]
  loading: boolean
  error: string | null
  refetch: () => void
}

const OrganizationOptionsContext = createContext<OrganizationOptionsContextType | undefined>(undefined)

interface OrganizationOptionsProviderProps {
  children: ReactNode
}

// Global cache to prevent multiple API calls within the same page load
// Cache expires after a short time to ensure fresh data on navigation
const CACHE_TTL = 30000 // 30 seconds - enough to prevent duplicate calls, short enough to get fresh data

type CacheEntry = {
  options: ClaimedByOption[]
  loading: boolean
  error: string | null
  promise: Promise<void> | null
  timestamp: number | null
}

const createCacheEntry = (): CacheEntry => ({
  options: [],
  loading: false,
  error: null,
  promise: null,
  timestamp: null,
})

const cacheStore: Record<string, CacheEntry> = {}

const getCacheEntry = (key: string): CacheEntry => {
  if (!cacheStore[key]) {
    cacheStore[key] = createCacheEntry()
  }
  return cacheStore[key]
}

export function OrganizationOptionsProvider({ children }: OrganizationOptionsProviderProps) {
  const { regionId, isLoading: regionLoading } = useRegionId()
  const cacheKey = regionId ? `region-${regionId}` : 'global'
  const initialCache = getCacheEntry(cacheKey)

  const [options, setOptions] = useState<ClaimedByOption[]>(initialCache.options)
  const [loading, setLoading] = useState(initialCache.loading)
  const [error, setError] = useState<string | null>(initialCache.error)

  const syncStateFromCache = useCallback(() => {
    const cache = getCacheEntry(cacheKey)
    setOptions(cache.options)
    setLoading(cache.loading)
    setError(cache.error)
  }, [cacheKey])

  const fetchOptions = useCallback(async () => {
    if (regionLoading) return

    const cache = getCacheEntry(cacheKey)
    const now = Date.now()
    const isCacheExpired = cache.timestamp === null || (now - cache.timestamp) > CACHE_TTL

    if (isCacheExpired && cache.options.length > 0) {
      cache.options = []
      cache.timestamp = null
      cache.error = null
    }

    if (cache.promise) {
      await cache.promise
      syncStateFromCache()
      return
    }

    if (cache.options.length > 0 && !cache.error && !isCacheExpired) {
      syncStateFromCache()
      return
    }

    cache.loading = true
    setLoading(true)

    cache.promise = (async () => {
      try {
        const params = new URLSearchParams()
        if (regionId) {
          params.set('regionId', regionId.toString())
        }

        const url = `/organizations/options${params.toString() ? `?${params.toString()}` : ''}`
        const res = await authFetchJson<any[]>(url)

        if (!res.success) {
          throw new Error(res.message || 'Failed to fetch organization options')
        }

        const data = (res.data as any)?.data ?? res.data
        const organizations = Array.isArray(data) ? data : []

        const allOptions: ClaimedByOption[] = [
          { label: 'Unclaimed', value: 'Unclaimed' },
          { label: 'No Need', value: 'No Need' },
          { label: 'Homeowner', value: 'Homeowner' },
          ...organizations.map((org: any) => ({
            label: getClaimedByDisplayName(org.label),
            value: org.label,
          })),
        ]

        cache.options = allOptions
        cache.error = null
        cache.loading = false
        cache.timestamp = Date.now()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred'
        cache.error = errorMessage
        cache.loading = false
        cache.options = [
          { label: 'Unclaimed', value: 'Unclaimed' },
          { label: 'No Need', value: 'No Need' },
          { label: 'Homeowner', value: 'Homeowner' }
        ]
      } finally {
        cache.promise = null
      }
    })()

    await cache.promise
    syncStateFromCache()
  }, [cacheKey, regionId, regionLoading, syncStateFromCache])

  const refetch = useCallback(() => {
    cacheStore[cacheKey] = createCacheEntry()
    syncStateFromCache()
    fetchOptions()
  }, [cacheKey, fetchOptions, syncStateFromCache])

  useEffect(() => {
    syncStateFromCache()
  }, [cacheKey, syncStateFromCache])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  const value: OrganizationOptionsContextType = {
    options,
    loading,
    error,
    refetch
  }

  return (
    <OrganizationOptionsContext.Provider value={value}>
      {children}
    </OrganizationOptionsContext.Provider>
  )
}

export function useOrganizationOptions() {
  const context = useContext(OrganizationOptionsContext)
  if (context === undefined) {
    throw new Error('useOrganizationOptions must be used within an OrganizationOptionsProvider')
  }
  return context
}

// Backward compatibility
export const useClaimedByOptions = useOrganizationOptions
